import { useCallback, useEffect, useRef, useState } from 'react';
import type { AuthContextValue, AuthUser } from './AuthContext';
import { AuthContext } from './AuthContext';
import { isPortalHost, portalUrl, restartPortalLogin } from './portal';

interface SessionResponse {
	authenticated: boolean;
	user?: { email: string; roles: string[]; isAdmin: boolean };
	reason?: string;
	error?: string;
	portalUrl?: string | null;
}

/** Re-check the session 1 minute before the 15-minute access token expires. */
const REFRESH_INTERVAL_MS = 14 * 60 * 1000;

/**
 * Cookie-based auth provider for production.
 * On mount, calls GET /api/auth/session to verify the HttpOnly session cookie.
 * Redirects to /api/auth/login if unauthenticated.
 * Re-fetches /api/auth/session every 14 minutes so the server can refresh
 * the access token transparently before it expires.
 *
 * Distinguishes two error types:
 * - Auth callback failure (?error= in URL): shows error with "Try again" → login
 * - Transient server error (5xx/network): shows error with "Retry" → re-checks session,
 *   does NOT clear cookies or redirect to login, keeps refresh interval running
 */
export function CookieAuthProvider({ children }: { children: React.ReactNode }) {
	const [user, setUser] = useState<AuthUser | null>(null);
	const [ready, setReady] = useState(false);
	const [transientError, setTransientError] = useState<string | null>(null);
	const [accessDenied, setAccessDenied] = useState(false);
	const [platformUrl, setPlatformUrl] = useState(portalUrl());
	const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
	// Track whether user is set without causing re-renders of checkSession
	const hasUserRef = useRef(false);

	// Auth callback failure — detected from URL, stops all session checks
	const [authError] = useState<string | null>(() => {
		const params = new URLSearchParams(window.location.search);
		const err = params.get('error');
		return err ? `Authentication failed: ${err}` : null;
	});

	const checkSession = useCallback(async (isInitial = false) => {
		try {
			const portal = isPortalHost();
			const endpoint = portal ? '/api/admin/session' : '/api/auth/session';
			const res = await fetch(endpoint, { credentials: 'same-origin' });

			if (portal && (res.status === 401 || res.redirected)) {
				restartPortalLogin();
				return;
			}

			if (res.status === 403) {
				setAccessDenied(true);
				setTransientError(null);
				return;
			}

			// Transient server error — don't redirect, don't clear cookies
			if (res.status >= 500) {
				if (isInitial && !hasUserRef.current) {
					setTransientError('Unable to reach the auth service. Please try again.');
				}
				return;
			}

			const data: SessionResponse = await res.json();
			setTransientError(null);
			setAccessDenied(false);
			if (data.portalUrl) setPlatformUrl(data.portalUrl);

			if (data.authenticated && data.user) {
				hasUserRef.current = true;
				setUser({
					email: data.user.email,
					name: data.user.email,
					isAdmin: data.user.isAdmin,
					hasRoles: data.user.roles.length > 0,
				});
			} else if (isInitial && portal) {
				restartPortalLogin();
			} else if (isInitial) {
				redirectToLogin();
			}
		} catch {
			if (isInitial && !hasUserRef.current) {
				setTransientError('Unable to reach the server. Please check your connection.');
			}
		}
	}, []); // No dependencies — uses ref for user check, no stale closures

	useEffect(() => {
		if (authError) {
			setReady(true);
			return;
		}

		checkSession(true).finally(() => setReady(true));

		timerRef.current = setInterval(() => checkSession(false), REFRESH_INTERVAL_MS);

		return () => {
			if (timerRef.current !== null) {
				clearInterval(timerRef.current);
			}
		};
	}, [checkSession, authError]);

	const signOut = useCallback(async () => {
		if (isPortalHost()) {
			window.location.assign(platformUrl);
			return;
		}
		try {
			const csrfRes = await fetch('/api/auth/csrf-token', { credentials: 'same-origin' });
			const { csrfToken } = await csrfRes.json();

			await fetch('/api/auth/logout', {
				method: 'POST',
				credentials: 'same-origin',
				headers: { 'x-csrf-token': csrfToken },
			});
			// Always redirect to our own login — the WorkOS sign-out redirect
			// is a shared global setting that points to the main product.
			redirectToLogin();
		} catch {
			redirectToLogin();
		}
	}, [platformUrl]);

	if (!ready) return null;

	if (authError) {
		return (
			<ErrorPage
				title="Sign-in failed"
				message={authError}
				action="Try again"
				onAction={() => {
					window.location.href = '/api/auth/login';
				}}
			/>
		);
	}

	if (transientError && !user) {
		return (
			<ErrorPage
				title="Connection issue"
				message={transientError}
				action="Retry"
				onAction={() => {
					setTransientError(null);
					checkSession(true);
				}}
			/>
		);
	}

	if (accessDenied) {
		return (
			<ErrorPage
				title="No access"
				message="Your Data Platform account does not have a Measurements admin or viewer role."
				action="Data Platform"
				onAction={() => window.location.assign(platformUrl)}
			/>
		);
	}

	if (!user) return null;

	// Authenticated but no roles — show access denied page
	if (!user.hasRoles) {
		return (
			<ErrorPage
				title="No access"
				message={`You're signed in as ${user.email} but you don't have a role in this organization. Ask an admin to assign you the admin or viewer role.`}
				action="Sign out"
				onAction={signOut}
			/>
		);
	}

	const value: AuthContextValue = {
		user,
		signOut,
		accountActionLabel: isPortalHost() ? 'Data Platform' : 'Sign out',
	};
	return <AuthContext value={value}>{children}</AuthContext>;
}

function redirectToLogin() {
	window.location.href = `/api/auth/login?return_to=${encodeURIComponent(window.location.pathname)}`;
}

function ErrorPage({
	title,
	message,
	action,
	onAction,
}: {
	title: string;
	message: string;
	action: string;
	onAction: () => void;
}) {
	return (
		<div
			style={{
				padding: '2rem',
				textAlign: 'center',
				fontFamily: 'system-ui',
				maxWidth: '480px',
				margin: '4rem auto',
			}}
		>
			<h2 style={{ marginBottom: '0.5rem' }}>{title}</h2>
			<p style={{ color: '#666', marginBottom: '1.5rem' }}>{message}</p>
			<button
				type="button"
				onClick={onAction}
				style={{
					padding: '0.5rem 1rem',
					border: '1px solid #ccc',
					borderRadius: '4px',
					cursor: 'pointer',
					background: '#f5f5f5',
				}}
			>
				{action}
			</button>
		</div>
	);
}
